import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Bucket, Check, ChevronLeft, ChevronRight, FileDown, GearWrench, Heart, ImagePlaceholder, Jar, Maximize, Minus, Plus, Pouch, ShieldCheck, Telegram, Tin, Tray, Truck, X } from "./icons";
import Breadcrumbs from "./Breadcrumbs";
import { useRFQ } from "../context/RFQContext";
import type { ContainerType, Product } from "../types";
import { CATEGORY_LABELS, CONTAINER_TYPE_LABELS } from "../types";
import { COMMON_SPEC_FIELDS, SUBCATEGORY_SPEC_FIELDS } from "../data/specFields";
import { CONTACT_TELEGRAM_URL } from "../data/contact";
import { getProductDisplayText } from "../lib/productDisplay";
import { getRecommendedAccessories } from "../lib/rationalAccessories";
import { trackInteraction } from "../lib/trackInteraction";
import ProductGrid from "./ProductGrid";
import ProductCard from "./ProductCard";
import {
  buildProductGroups,
  extractVariantAxes,
  findVariantMatch,
  getProductAxisValue,
} from "../lib/productGroups";
import { useLanguage } from "../context/LanguageContext";

type Tab = "description" | "specs" | "video" | "brochure" | "accessories";


const CONTAINER_TYPE_ICONS: Record<ContainerType, typeof Jar> = {
  glass: Jar,
  tin: Tin,
  bucket: Bucket,
  tray: Tray,
  pouch: Pouch,
};

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Subcategory labels (e.g. "Тарелки") are capitalized for their own use as
// a heading/filter option, but read oddly capitalized mid-sentence in the
// "Все тарелки {brand}" quick-links below — lowercased there only.
function lowercaseFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

// Fallback for a characteristics key with no translated label in
// specFields.ts (e.g. "capacity_kg") — every real field should get a
// proper Russian label added there, but until it is, this keeps the row
// legible instead of leaking a raw snake_case key onto the page.
function prettifyKey(key: string): string {
  return capitalize(key.replace(/_/g, " "));
}

// Splits a freeform spec string into a label/value pair so every row on the
// characteristics tab renders in the same two-column style, site-wide —
// this is a rendering rule, not per-product data, so fixing it here covers
// every brand's catalog at once. `matched: false` tells the caller this
// phrase has no natural label/value shape at all (a full sentence like
// "Можно мыть в посудомоечной машине", not an attribute) — those render
// separately, as plain bullets, rather than faking a value column with an
// empty string (see productDetailAdditionalInfo below).
function splitSpec(spec: string): { label: string; value: string; matched: boolean } {
  const colonMatch = spec.match(/^([^:]+):\s*(.+)$/);
  if (colonMatch) return { label: colonMatch[1].trim(), value: colonMatch[2].trim(), matched: true };

  // "10 противней" -> Противней: 10 — a leading count followed by the noun
  // it counts.
  const numberFirstMatch = spec.match(/^(\d+(?:[.,]\d+)?)\s+([а-яёА-ЯЁ].+)$/);
  if (numberFirstMatch) {
    return { label: capitalize(numberFirstMatch[2].trim()), value: numberFirstMatch[1].trim(), matched: true };
  }

  // "Управление S-Control" / "модуль IOT" -> a descriptive lead-in followed
  // by a model/system code in Latin characters.
  const codeMatch = spec.match(/^([а-яёА-ЯЁ][а-яёА-ЯЁ\s]*?)\s+([A-Z][A-Za-z0-9-]*(?:\s+[A-Za-zА-Яа-я0-9-]+)*)$/);
  if (codeMatch) return { label: capitalize(codeMatch[1].trim()), value: codeMatch[2].trim(), matched: true };

  // "Ручная загрузка" -> Загрузка: Ручная — a leading Russian adjective
  // (agreement suffix -ая/-ой/-ый/etc.) describing the noun that follows;
  // the noun is the general category so it becomes the label, and the
  // adjective becomes the specific value, matching the noun-first
  // convention used above for coded/numeric specs.
  const ADJ_SUFFIX = /(ая|яя|ое|ее|ый|ий|ой|ые|ие|ых)$/i;
  const adjNounMatch = spec.match(/^([а-яёА-ЯЁ]+)\s+([а-яёА-ЯЁ][а-яёА-ЯЁ\s]*)$/);
  if (adjNounMatch) {
    const [, first, rest] = adjNounMatch;
    const restWords = rest.trim().split(/\s+/);
    if (ADJ_SUFFIX.test(first) && !ADJ_SUFFIX.test(restWords[restWords.length - 1])) {
      return { label: capitalize(rest.trim()), value: capitalize(first), matched: true };
    }
  }

  // No natural split — not rendered as a fake table row (see caller).
  return { label: spec, value: "", matched: false };
}

export default function ProductDetailPage() {
  const { t } = useLanguage();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [selectedAxisValues, setSelectedAxisValues] = useState<Record<string, string>>({});
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Swipe-to-change on the main product photo (mobile) — the dots still
  // work too, this just adds the gesture users actually reach for first.
  const imageTouchStartXRef = useRef<number | null>(null);
  const handleImageTouchStart = (e: React.TouchEvent) => {
    imageTouchStartXRef.current = e.touches[0].clientX;
  };
  const handleImageTouchEnd = (e: React.TouchEvent, imageCount: number) => {
    const startX = imageTouchStartXRef.current;
    imageTouchStartXRef.current = null;
    if (startX === null) return;
    const deltaX = e.changedTouches[0].clientX - startX;
    const SWIPE_THRESHOLD_PX = 40;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
    setActiveImage((i) =>
      deltaX < 0 ? Math.min(i + 1, imageCount - 1) : Math.max(i - 1, 0)
    );
  };
  const [activeTab, setActiveTab] = useState<Tab>("description");
  const [quantity, setLocalQuantity] = useState(1);
  const { isSelected, addItem } = useRFQ();
  // Purely a customer preference for the quote request — GN capacity within
  // a LUNAR depth isn't a distinct SKU/price (every combination is "Цена по
  // запросу"), so this never navigates, it just tracks which pill is lit.
  const [selectedGn, setSelectedGn] = useState<string | null>(null);

  // Thumbnail strip: hovering near its top/bottom edge auto-scrolls that
  // direction (like a drag-and-drop file list), and the bottom fade hides
  // itself once there's nothing left below to scroll to.
  const thumbColRef = useRef<HTMLDivElement>(null);
  const similarScrollRef = useRef<HTMLDivElement>(null);
  const scrollSimilarBy = (direction: 1 | -1) => {
    const el = similarScrollRef.current;
    if (!el) return;
    // Scrolls by roughly one card's width rather than the whole track, so
    // "press an arrow" reveals the next item instead of jumping past
    // several at once. Cards sit flush (shared 1px borders, no gap), so
    // the card's own offsetWidth already is the step.
    const card = el.querySelector<HTMLElement>("[data-similar-card]");
    const step = card?.offsetWidth ?? el.clientWidth * 0.8;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };
  const [canScrollDown, setCanScrollDown] = useState(false);
  const autoScrollRef = useRef<number>();

  const stopThumbAutoScroll = () => {
    window.clearInterval(autoScrollRef.current);
    autoScrollRef.current = undefined;
  };

  const handleThumbMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const EDGE = 32;
    const direction = offsetY < EDGE ? -1 : rect.height - offsetY < EDGE ? 1 : 0;
    stopThumbAutoScroll();
    if (direction !== 0) {
      autoScrollRef.current = window.setInterval(() => {
        thumbColRef.current?.scrollBy({ top: direction * 6 });
      }, 16);
    }
  };

  useEffect(() => stopThumbAutoScroll, []);

  // Returns to the catalog with whatever filters were active when the user
  // left it, instead of always landing on the unfiltered /catalog. Prefers
  // the `from` URL param (set by ProductCard's link) since that survives a
  // link opened in a new tab or shared/bookmarked — sessionStorage doesn't,
  // it's scoped to the tab that actually browsed the catalog. Falls back to
  // sessionStorage for entry points that don't set `from` (e.g. related/
  // accessory product links elsewhere on this page).
  const [searchParams] = useSearchParams();
  const [catalogBackHref] = useState(() => {
    const fromParam = searchParams.get("from");
    if (fromParam && fromParam.startsWith("/catalog")) return fromParam;
    return sessionStorage.getItem("lastCatalogUrl") ?? "/catalog";
  });

  // The full catalog is fetched exactly once, live (not the old
  // /products.json build snapshot — a build-time export of the seed data
  // files with no connection to the live database, so an admin edit never
  // used to show up here until the next deploy). Every subsequent
  // navigation between product pages (variant swap, related/accessory
  // links) then resolves instantly from this already-loaded list instead
  // of re-fetching and blanking the page — that per-navigation blank/
  // loading flash was the "screen glitch" between switching products.
  // GET /api/products sets a short Cache-Control, so repeat loads within
  // that window are still fast without paying the live-query cost again.
  useEffect(() => {
    fetch("/api/products")
      .then((res) => res.json())
      .then((all: Product[]) => {
        setAllProducts(all);
        setCatalogLoaded(true);
      })
      .catch(() => {});
  }, []);

  const product = catalogLoaded ? (allProducts.find((p) => p.id === productId) ?? null) : undefined;

  // Feeds the same interest-tracking signal category filtering does (see
  // useCatalogFilters), so simply browsing product pages — not just
  // filtering the catalog or searching — builds up personalization too.
  // product_view (the specific id, not just the category) is what lets the
  // "Рекомендуем" widget below find real "other visitors who viewed this
  // also viewed" matches instead of only coarse same-category ones.
  useEffect(() => {
    if (product) {
      trackInteraction("category_view", product.category);
      trackInteraction("product_view", product.id);
    }
  }, [product?.id]);

  // Brand-qualified, city-agnostic title for search ("купить в Казахстане"
  // covers both Almaty and Astana without needing per-city product pages).
  useEffect(() => {
    if (!product) return;
    const previousTitle = document.title;
    document.title = `${product.brand} ${product.name} купить в Казахстане (цена, доставка, гарантия) — BARCRAFT`;
    return () => {
      document.title = previousTitle;
    };
  }, [product?.id]);

  const accessories = product ? getRecommendedAccessories(product, allProducts) : [];
  // Server-computed (see GET /api/products/:id/recommendations) so it can
  // rank by actual co-interaction with this specific product first, falling
  // back to subcategory/brand/category similarity only when that
  // behavioral signal is thin. Feeds the "Похожие товары" section below.
  const [recommended, setRecommended] = useState<Product[]>([]);
  useEffect(() => {
    if (!product) {
      setRecommended([]);
      return;
    }
    fetch(`/api/products/${product.id}/recommendations`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setRecommended)
      .catch(() => setRecommended([]));
  }, [product?.id]);
  // Ranked fallback chain, each tier only used to fill gaps left by the
  // tier before it: co-interaction data first (best signal), then same
  // subcategory + brand (e.g. another Aroma can seamer), then same
  // subcategory alone, and only as a last resort the whole top-level
  // category — which is broad enough (e.g. all of "Кофейное & Барное") to
  // surface genuinely unrelated items, so it's deliberately ranked lowest
  // rather than used as the primary source the way it was before.
  const similarCandidates = product
    ? [
        ...recommended,
        ...allProducts.filter(
          (p) => p.id !== product.id && p.subcategory === product.subcategory && p.brand === product.brand
        ),
        ...allProducts.filter((p) => p.id !== product.id && p.subcategory === product.subcategory),
        ...allProducts.filter((p) => p.id !== product.id && p.category === product.category),
      ].filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i)
    : [];
  const similarGroups = buildProductGroups(similarCandidates).slice(0, 10);
  const accessoryGroups = buildProductGroups(accessories);

  useEffect(() => {
    setActiveImage(0);
    setActiveTab("description");
    setLocalQuantity(1);
  }, [productId]);

  // Tracks whether the thumbnail strip has more content below the fold, so
  // the bottom fade only shows while there's actually something to scroll to.
  useEffect(() => {
    const el = thumbColRef.current;
    if (!el) return;
    const update = () => setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [product?.id, product?.images]);

  // Re-derives the selector's values whenever the loaded product (or the
  // catalog it's grouped against) changes, so the pills always reflect the
  // SKU actually on screen instead of carrying over a stale selection.
  useEffect(() => {
    if (!product || allProducts.length === 0) return;
    const group = buildProductGroups(allProducts).find((g) =>
      g.products.some((p) => p.id === product.id)
    );
    if (!group) return;
    const axes = extractVariantAxes(group);
    const initial: Record<string, string> = {};
    for (const axis of axes) initial[axis.key] = getProductAxisValue(product, axis.key);
    setSelectedAxisValues(initial);
  }, [product, allProducts]);

  // Esc/arrow keys only need to do anything while the lightbox is open — the
  // listener is attached/removed with it rather than living for the page's
  // whole lifetime.
  useEffect(() => {
    if (!lightboxOpen || !product) return;
    const imageCount = product.images?.length || 1;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxOpen(false);
      else if (e.key === "ArrowLeft") {
        setActiveImage((prev) => (prev - 1 + imageCount) % imageCount);
      } else if (e.key === "ArrowRight") {
        setActiveImage((prev) => (prev + 1) % imageCount);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen, product]);

  if (product === null) {
    return <Navigate to="/catalog" replace />;
  }

  if (product === undefined) {
    return (
      <section className="bg-white pb-24 pt-24 md:pb-32 md:pt-32">
        <div className="container-industrial">
          <p className="text-sm text-steel-500">{t("productLoading")}</p>
        </div>
      </section>
    );
  }

  const images = (
    product.images && product.images.length > 0 ? product.images : [product.image]
  ).filter(Boolean);
  const added = isSelected(product.id);
  const { cleanedName, nameDetail, cleanedModel, showModel } = getProductDisplayText(
    product.name,
    product.model,
    product.brand
  );
  const sku = typeof product.characteristics?.sku === "string" ? product.characteristics.sku : undefined;
  const modelLine = showModel ? cleanedModel : sku;

  const myGroup = buildProductGroups(allProducts).find((g) =>
    g.products.some((p) => p.id === product.id)
  );
  const variantAxes = myGroup ? extractVariantAxes(myGroup) : [];
  const showSelector = variantAxes.length > 0;
  const activeMatch = myGroup ? findVariantMatch(myGroup, variantAxes, selectedAxisValues) : product;

  // A brochure covers the whole product line — every capacity/color variant
  // in the picker — not just the one specific SKU it happened to be
  // uploaded to. An admin uploads it once, to any single variant, and it
  // keeps showing no matter which option the customer has selected.
  const sharedBrochureUrl = myGroup
    ? myGroup.products.find((p) => p.brochureUrl)?.brochureUrl
    : product.brochureUrl;

  // Derives selectable GN-capacity pills from a "N-M GN ..." range
  // characteristic (e.g. LUNAR's "2-6 GN 1/1") instead of a hardcoded list,
  // so any product with that shape of range gets the same picker for free.
  const gnRangeRaw =
    typeof product.characteristics?.gn_range === "string" ? product.characteristics.gn_range : undefined;
  const gnMatch = gnRangeRaw?.match(/^(\d+)\s*-\s*(\d+)\s*GN/);
  const gnOptions = gnMatch
    ? Array.from({ length: Number(gnMatch[2]) - Number(gnMatch[1]) + 1 }, (_, i) => `${Number(gnMatch[1]) + i} GN`)
    : [];
  const activeGn = selectedGn && gnOptions.includes(selectedGn) ? selectedGn : gnOptions[0];

  function handleAxisChange(axisKey: string, value: string) {
    if (!myGroup) return;
    const next = { ...selectedAxisValues, [axisKey]: value };
    setSelectedAxisValues(next);
    const match = findVariantMatch(myGroup, variantAxes, next);
    if (match && match.id !== product!.id) {
      navigate(`/catalog/${match.id}`, { replace: true });
    }
  }

  const specFieldDefs = [
    ...COMMON_SPEC_FIELDS,
    ...(product.subcategory ? SUBCATEGORY_SPEC_FIELDS[product.subcategory] ?? [] : []),
  ];
  // Applications render as a row in the same table as the rest of the
  // characteristics (see the clean single-table reference layout), rather
  // than a separate list — it's just another fact about the product.
  const applicationsEntry =
    product.applications && product.applications.length > 0
      ? { key: "__applications", label: t("productDetailApplications"), value: product.applications.join(", ") }
      : null;
  const characteristicEntries = [
    ...(applicationsEntry ? [applicationsEntry] : []),
    ...(Object.entries(product.characteristics ?? {}) || []).map(([key, value]) => {
      const def = specFieldDefs.find((field) => field.key === key);
      return {
        key,
        label: def?.label ?? prettifyKey(key),
        value: def?.unit ? `${value} ${def.unit}` : String(value),
      };
    }),
  ];
  // Specs render in the same label/value row style as the characteristics
  // above them (see splitSpec). Skipped when a spec's label exactly
  // duplicates one already shown above (e.g. a "Тип: …" spec next to an
  // energy_type characteristic). A spec with no natural label/value split —
  // a full sentence, not an attribute — never joins that aligned table (it
  // has nothing to put in the right-hand column); it's listed separately
  // under "Дополнительная информация" instead, so every row that IS in the
  // table has both a name and a value, with no exceptions.
  const characteristicLabels = new Set((characteristicEntries || []).map((e) => e.label.toLowerCase()));
  const splitSpecs = (product.specs || [])
    .map(splitSpec)
    .filter((entry) => !characteristicLabels.has(entry.label.toLowerCase()));
  const specEntries = splitSpecs.filter((entry) => entry.matched);
  const additionalInfoEntries = splitSpecs.filter((entry) => !entry.matched).map((entry) => entry.label);
  const hasContainerTypes = (product.containerTypes?.length ?? 0) > 0;
  const hasCharacteristics =
    characteristicEntries.length > 0 ||
    specEntries.length > 0 ||
    additionalInfoEntries.length > 0 ||
    hasContainerTypes;

  const availableTabs: { key: Tab; label: string }[] = [
    ...(product.description
      ? [{ key: "description" as const, label: t("productDetailTabDescription") }]
      : []),
    ...(hasCharacteristics ? [{ key: "specs" as const, label: t("productDetailTabSpecs") }] : []),
    ...(product.videoId ? [{ key: "video" as const, label: t("productDetailTabVideo") }] : []),
    ...(sharedBrochureUrl
      ? [{ key: "brochure" as const, label: t("productDetailTabBrochure") }]
      : []),
    ...(accessories.length > 0
      ? [{ key: "accessories" as const, label: t("productDetailTabAccessories") }]
      : []),
  ];
  const effectiveTab: Tab | undefined = availableTabs.some((t) => t.key === activeTab)
    ? activeTab
    : availableTabs[0]?.key;

  return (
    <section className="bg-white pb-24 pt-24 md:pb-32 md:pt-32">
      <Breadcrumbs
        items={[
          { label: t("catalogLabel"), href: catalogBackHref },
          ...(product.category
            ? [{ label: CATEGORY_LABELS[product.category], href: `/catalog?category=${product.category}` }]
            : []),
          ...(product.subcategory ? [{ label: product.subcategory }] : []),
        ]}
      />
      <div className="container-industrial pt-8">
        {/* Below xl this is a simple stacked column at the container's full
            width — fine, since the services block also stacks full-width
            there. At xl+, the two blocks stop stretching to fill the
            container (that stretch was the actual bug: a `1fr` track plus
            a non-growing text column left dead space between the buy box
            and the services column) and instead take their own natural
            widths, centered as one unit via justify-center — so the margin
            outside the product image matches the margin outside the
            services column exactly, on any screen width. */}
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-center xl:gap-6">
          {/* Capped at 820px on genuinely wide screens (matches the services
              column's own fixed width for the centered-margins math below),
              but shrinks below that via min() on narrower xl+ viewports
              (1280-1360px laptops) — a bare xl:w-[820px] xl:shrink-0 here
              demanded 820+380+24px gap+128px container padding = 1352px
              minimum, which overflowed and got silently clipped (see
              overflow-x: clip in index.css) on exactly the viewports the
              xl breakpoint is supposed to serve. */}
          <div className="flex min-w-0 flex-col gap-6 xl:sticky xl:top-28 xl:w-[min(820px,calc(100vw-380px-1.5rem-8rem))]">
            <div className="flex flex-col gap-6 py-4 sm:py-6 sm:flex-row">
              <div className="flex max-w-full flex-col gap-3 overflow-hidden sm:shrink-0 sm:flex-row">
                <div className="relative hidden sm:block sm:w-8 sm:shrink-0">
                  {images.length > 1 && (
                    <>
                      <div
                        ref={thumbColRef}
                        onMouseMove={handleThumbMouseMove}
                        onMouseLeave={stopThumbAutoScroll}
                        className="no-scrollbar flex max-w-full gap-1.5 overflow-x-auto py-1 sm:max-h-[315px] sm:flex-col sm:overflow-x-visible sm:overflow-y-auto"
                      >
                        {(images || []).map((img, i) => (
                          <button
                            key={`${img}-${i}`}
                            type="button"
                            onClick={() => setActiveImage(i)}
                            className={`h-7 w-7 shrink-0 overflow-hidden border transition-colors duration-200 ease-engineered sm:h-8 sm:w-8 ${
                              activeImage === i ? "border-accent" : "border-steel-200 hover:border-steel-400"
                            }`}
                          >
                            <img
                              src={img}
                              alt={`${product.name} — фото ${i + 1}`}
                              draggable={false}
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                      {canScrollDown && (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-8 bg-gradient-to-b from-transparent to-white sm:block" />
                      )}
                    </>
                  )}
                </div>
                <div
                  onTouchStart={handleImageTouchStart}
                  onTouchEnd={(e) => handleImageTouchEnd(e, images.length)}
                  className="group relative flex aspect-[4/3] w-full touch-pan-y items-center justify-center overflow-hidden sm:w-[420px] sm:shrink-0"
                >
                  {images.length > 0 ? (
                    <img
                      src={images[activeImage]}
                      alt={product.name}
                      draggable={false}
                      // Product pages' hero photo is reliably the LCP
                      // element (Cloudflare flagged ~3.5-3.9s LCP) — eager
                      // + high fetch priority gets the browser to request
                      // it immediately rather than at default priority.
                      loading="eager"
                      fetchPriority="high"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <ImagePlaceholder size={64} className="text-steel-300" />
                  )}
                  {product.stockQuantity !== undefined && product.stockQuantity > 0 && (
                    <span className="absolute right-2 top-2 bg-emerald-700 px-2 py-1 text-[9px] font-semibold uppercase tracking-wide2 text-white">
                      {t("inStockBadge")}
                    </span>
                  )}
                  {/* Desktop only — mobile already has native pinch-to-zoom
                      on the photo, so a separate lightbox trigger there is
                      just clutter. Hidden entirely when there's no image to
                      zoom into. */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLightboxOpen(true);
                    }}
                    aria-label={t("productDetailExpandImage")}
                    className={`absolute bottom-3 right-3 h-9 w-9 items-center justify-center border border-steel-200 bg-white/85 text-ink shadow-sm backdrop-blur-sm transition-colors duration-200 ease-engineered hover:border-accent hover:text-accent ${images.length > 0 ? "hidden sm:flex" : "hidden"}`}
                  >
                    <Maximize size={16} />
                  </button>
                  {images.length > 1 && (
                    <>
                      {/* Desktop-only, same resting/hover styling as the
                          expand button below (always-visible white/85
                          circle with a steel border, accent on hover) —
                          mobile relies on the swipe gesture and dot
                          indicators instead. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveImage((prev) => (prev - 1 + images.length) % images.length);
                        }}
                        className="absolute left-3 hidden h-9 w-9 items-center justify-center border border-steel-200 bg-white/85 text-ink shadow-sm backdrop-blur-sm transition-colors duration-200 ease-engineered hover:border-accent hover:text-accent sm:flex"
                        aria-label="Previous image"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveImage((prev) => (prev + 1) % images.length);
                        }}
                        className="absolute right-3 hidden h-9 w-9 items-center justify-center border border-steel-200 bg-white/85 text-ink shadow-sm backdrop-blur-sm transition-colors duration-200 ease-engineered hover:border-accent hover:text-accent sm:flex"
                        aria-label="Next image"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </>
                  )}
                </div>

                {images.length > 1 && (
                  <div className="flex items-center justify-center gap-1 sm:hidden">
                    {(images || []).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveImage(i)}
                        aria-label={`${t("productImageLabel")} ${i + 1}`}
                        className="flex h-4 w-4 items-center justify-center"
                      >
                        <span
                          className={`transition-all duration-200 ease-engineered ${
                            activeImage === i ? "h-1.5 w-1.5 bg-accent" : "h-1.5 w-1.5 bg-steel-300"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-4 xl:w-[320px] xl:flex-none">
                <div>
                  {product.brand && (
                    <p className="text-xs font-semibold uppercase tracking-wide2 text-accent">
                      {product.brand}
                    </p>
                  )}
                  <h1 className="mt-1 font-sans text-xl font-semibold leading-tight tracking-[-0.03em] text-ink">
                    {cleanedName}
                  </h1>
                  {nameDetail && <p className="mt-1 text-sm text-steel-500">{nameDetail}</p>}
                  {modelLine && (
                    <p className="mt-1 text-sm uppercase tracking-wide2 text-steel-500">{modelLine}</p>
                  )}
                </div>

                {typeof product.characteristics?.compatible_with === "string" && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide2 text-steel-500">
                      {t("productCompatibleWithLabel")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(String(product.characteristics.compatible_with)
                        .split(",") || [])
                        .map((item) => item.trim())
                        .filter(Boolean)
                        .map((item) => (
                          <span
                            key={item}
                            className="border border-steel-200 px-2.5 py-1 text-xs text-ink"
                          >
                            {item}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {showSelector && (
                  <div className="flex flex-col gap-4 border-t border-steel-100 pt-4">
                    {(variantAxes || []).map((axis) => {
                      const selectedOption = axis.options.find(
                        (opt) => opt.value === selectedAxisValues[axis.key]
                      );
                      return (
                      <div key={axis.key}>
                        <p className="text-xs font-semibold uppercase tracking-wide2 text-steel-500">
                          {axis.label}
                          {selectedOption && (
                            <span className="ml-1 normal-case tracking-normal text-ink">
                              {selectedOption.label}
                            </span>
                          )}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(axis.options || []).map((opt) =>
                            opt.image ? (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleAxisChange(axis.key, opt.value)}
                                title={opt.label}
                                aria-label={opt.label}
                                aria-pressed={selectedAxisValues[axis.key] === opt.value}
                                className={`h-9 w-9 shrink-0 overflow-hidden border-2 p-0.5 transition-colors duration-200 ease-engineered ${
                                  selectedAxisValues[axis.key] === opt.value
                                    ? "border-accent"
                                    : "border-transparent hover:border-steel-300"
                                }`}
                              >
                                <img
                                  src={opt.image}
                                  alt={opt.label}
                                  className="h-full w-full object-cover"
                                />
                              </button>
                            ) : (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleAxisChange(axis.key, opt.value)}
                                className={`border px-3 py-1.5 text-xs font-medium transition-colors duration-200 ease-engineered ${
                                  selectedAxisValues[axis.key] === opt.value
                                    ? "border-accent bg-accent text-white"
                                    : "border-steel-300 text-ink hover:border-accent hover:text-accent"
                                }`}
                              >
                                {opt.label}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}

                {gnOptions.length > 0 && (
                  <div className={showSelector ? "" : "border-t border-steel-100 pt-4"}>
                    <p className="text-xs font-semibold uppercase tracking-wide2 text-steel-500">
                      {t("productDetailCapacityGn")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(gnOptions || []).map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setSelectedGn(opt)}
                          className={`border px-3 py-1.5 text-xs font-medium transition-colors duration-200 ease-engineered ${
                            activeGn === opt
                              ? "border-accent bg-accent text-white"
                              : "border-steel-300 text-ink hover:border-accent hover:text-accent"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {showSelector && !activeMatch ? (
                  <div className="flex flex-col gap-3">
                    <p className="font-sans text-3xl font-semibold text-ink">{t("priceOnRequest")}</p>
                    <a
                      href="/contacts"
                      className="flex w-full items-center justify-center gap-2 border border-steel-300 px-6 py-4 text-xs font-semibold uppercase tracking-wide2 text-ink transition-colors duration-200 ease-engineered hover:border-accent hover:text-accent sm:w-auto"
                    >
                      {t("productDetailClarifyStock")}
                    </a>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      {product.price !== undefined && (
                        <div className="shrink-0 font-sans text-xs leading-snug text-steel-400">
                          <p>{t("productDetailPriceLabel")}</p>
                          <p className="text-accent underline decoration-dotted decoration-accent/60 underline-offset-2">
                            {t("productDetailPriceVatLabel")}
                          </p>
                        </div>
                      )}
                      <p className="font-sans text-3xl font-semibold text-ink">
                        {product.price !== undefined
                          ? `${(product.price * quantity).toLocaleString("ru-RU")} ₸`
                          : t("priceOnRequest")}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-fit shrink-0 bg-steel-100">
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={quantity}
                          onChange={(e) => {
                            const next = Math.trunc(Number(e.target.value));
                            setLocalQuantity(Number.isFinite(next) && next > 0 ? next : 1);
                          }}
                          aria-label={t("quantityLabel")}
                          className="w-10 min-w-0 bg-transparent pl-3 text-left text-sm font-semibold text-ink outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                        <div className="flex w-8 shrink-0 flex-col divide-y divide-steel-300 bg-steel-300 text-ink">
                          <button
                            type="button"
                            onClick={() => setLocalQuantity((q) => q + 1)}
                            aria-label={t("quantityIncrease")}
                            className="flex flex-1 items-center justify-center transition-colors duration-200 ease-engineered hover:bg-steel-400"
                          >
                            <Plus size={11} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setLocalQuantity((q) => Math.max(1, q - 1))}
                            aria-label={t("quantityDecrease")}
                            className="flex flex-1 items-center justify-center transition-colors duration-200 ease-engineered hover:bg-steel-400"
                          >
                            <Minus size={11} />
                          </button>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          for (let i = 0; i < quantity; i++) addItem(product);
                          setLocalQuantity(1);
                        }}
                        className={`flex h-11 flex-1 items-center justify-center gap-2 border px-6 text-xs font-semibold uppercase tracking-wide2 transition-colors duration-200 ease-engineered sm:flex-none ${
                          added
                            ? "border-accent bg-accent text-white hover:bg-accent-hover"
                            : "border-steel-300 text-ink hover:border-accent hover:text-accent"
                        }`}
                      >
                        {added ? <Check size={20} /> : <Plus size={20} />}
                        {added ? t("productAdded") : t("productAddToProjectFull")}
                      </button>
                    </div>
                  </>
                )}

                <div>
                  <p
                    className={`flex items-center gap-2 text-sm ${
                      product.stockQuantity !== undefined && product.stockQuantity > 0
                        ? "text-lime-600"
                        : "text-steel-600"
                    }`}
                  >
                    <Check size={20} strokeWidth={2} className="shrink-0" />
                    {product.stockQuantity !== undefined && product.stockQuantity > 0 ? (
                      <span>
                        <span className="font-bold">{t("productDetailStockPrefix")}</span>{" "}
                        {product.stockQuantity} {t("productDetailStockUnit")}
                      </span>
                    ) : (
                      t("productDetailBackorder")
                    )}
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-steel-500">
                    {product.leadTimeDays !== undefined && (
                      <>
                        {t("productDetailLeadTimePrefix")} {product.leadTimeDays}{" "}
                        {t("productDetailLeadTimeUnit")}
                        <br />
                      </>
                    )}
                    {t("productDetailReassurance")}
                  </p>
                </div>

                {(product.brand || product.subcategory) && (
                  <div className="mt-3 flex flex-col gap-0.5 border-t border-steel-200 pt-3">
                    {/* Plain inline flow (no flex) so the icon is just the
                        last inline glyph after the text. The final word and
                        the icon are further wrapped in a whitespace-nowrap
                        span so they wrap to a new line together as one unit
                        when the line runs out of room, instead of the arrow
                        alone getting orphaned onto its own line below. */}
                    {product.brand && (
                      <Link
                        to={`/catalog?brands=${encodeURIComponent(product.brand)}`}
                        className="block w-fit text-sm text-ink transition-colors duration-200 ease-engineered hover:text-accent"
                      >
                        {t("productDetailAllBrandProducts")}{" "}
                        <span className="whitespace-nowrap">
                          {product.brand}
                          <ChevronRight size={16} className="ml-1 inline-block align-text-bottom text-accent" />
                        </span>
                      </Link>
                    )}
                    {product.brand && product.subcategory && (
                      <Link
                        to={`/catalog?category=${product.category}&subcategory=${encodeURIComponent(product.subcategory)}&brands=${encodeURIComponent(product.brand)}`}
                        className="block w-fit text-sm text-ink transition-colors duration-200 ease-engineered hover:text-accent"
                      >
                        {t("productDetailAllSubcategoryPrefix")} {lowercaseFirst(product.subcategory)}{" "}
                        <span className="whitespace-nowrap">
                          {product.brand}
                          <ChevronRight size={16} className="ml-1 inline-block align-text-bottom text-accent" />
                        </span>
                      </Link>
                    )}
                    {product.subcategory && (
                      <Link
                        to={`/catalog?category=${product.category}&subcategory=${encodeURIComponent(product.subcategory)}`}
                        className="block w-fit text-sm text-ink transition-colors duration-200 ease-engineered hover:text-accent"
                      >
                        {t("productDetailAllSubcategoryPrefix")} {lowercaseFirst(product.subcategory)}{" "}
                        <span className="whitespace-nowrap">
                          {t("productDetailInCategorySuffix")}
                          <ChevronRight size={16} className="ml-1 inline-block align-text-bottom text-accent" />
                        </span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>

            {effectiveTab && (
              <div>
                <div className="no-scrollbar flex touch-pan-x gap-6 overflow-x-auto border-b border-steel-200">
                  {(availableTabs || []).map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={(e) => {
                        setActiveTab(tab.key);
                        // The tab row scrolls horizontally on narrow viewports
                        // (long labels like "Сопутствующие товары" don't fit);
                        // without this, a tab near the end lands partially
                        // clipped by the viewport edge instead of in view.
                        e.currentTarget.scrollIntoView({
                          behavior: "smooth",
                          inline: "nearest",
                          block: "nearest",
                        });
                      }}
                      className={`relative shrink-0 pb-3 text-sm transition-colors duration-200 ease-engineered ${
                        effectiveTab === tab.key
                          ? "font-semibold text-accent"
                          : "text-steel-500 hover:text-ink"
                      }`}
                    >
                      {tab.label}
                      {effectiveTab === tab.key && (
                        <span className="absolute inset-x-0 -bottom-px h-0.5 bg-accent" />
                      )}
                    </button>
                  ))}
                </div>

                <div className="pt-6">
                  {effectiveTab === "description" && (
                    <p className="whitespace-pre-line text-sm leading-relaxed text-steel-600">
                      {product.description}
                    </p>
                  )}

                  {effectiveTab === "specs" && (
                    <div className="flex flex-col gap-6">
                      {hasContainerTypes && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide2 text-steel-500">
                            {t("productDetailContainerTypes")}
                          </p>
                          <div className="mt-3 flex flex-wrap gap-5">
                            {(product.containerTypes || []).map((ct) => {
                              const Icon = CONTAINER_TYPE_ICONS[ct];
                              return (
                                <div key={ct} className="flex w-24 flex-col items-center gap-2 text-center">
                                  <Icon size={40} className="shrink-0 text-accent" strokeWidth={1.5} />
                                  <span className="bg-steel-50 px-2 py-1 text-[11px] leading-tight text-steel-600">
                                    {CONTAINER_TYPE_LABELS[ct]}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <dl className="flex flex-col gap-2.5">
                      {(characteristicEntries || []).map((entry) => (
                        <div
                          key={entry.key}
                          className="flex flex-row items-baseline justify-between gap-4 border-b border-steel-100 pb-2 text-sm sm:gap-16"
                        >
                          <dt className="shrink-0 text-steel-500">{entry.label}</dt>
                          <dd className="max-w-[55%] text-right text-ink">{entry.value}</dd>
                        </div>
                      ))}
                      {(specEntries || []).map((entry, i) => (
                        <div
                          key={`spec-${i}`}
                          className="flex flex-row items-baseline justify-between gap-4 border-b border-steel-100 pb-2 text-sm sm:gap-16"
                        >
                          <dt className="shrink-0 text-steel-500">{entry.label}</dt>
                          <dd className="max-w-[55%] text-right text-ink">{entry.value}</dd>
                        </div>
                      ))}
                      </dl>
                      {additionalInfoEntries.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide2 text-steel-500">
                            {t("productDetailAdditionalInfo")}
                          </p>
                          <ul className="mt-3 flex flex-col gap-2">
                            {additionalInfoEntries.map((text, i) => (
                              <li key={i} className="flex gap-2.5 text-sm text-ink">
                                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-steel-400" />
                                {text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {effectiveTab === "video" && product.videoId && (
                    <div className="aspect-video w-full">
                      <iframe
                        src={
                          /^\d+$/.test(product.videoId)
                            ? `https://player.vimeo.com/video/${product.videoId}`
                            : `https://www.youtube.com/embed/${product.videoId}`
                        }
                        title={`${product.name} — ${t("productDetailTabVideo").toLowerCase()}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="h-full w-full border border-steel-200"
                      />
                    </div>
                  )}

                  {effectiveTab === "brochure" && sharedBrochureUrl && (
                    <div className="flex flex-col gap-4">
                      <a
                        href={sharedBrochureUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="flex items-center gap-3 border border-steel-200 p-4 transition-colors duration-200 ease-engineered hover:border-accent"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-steel-100 text-steel-600">
                          <FileDown size={18} />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-ink">{t("productDetailDownloadBrochure")}</span>
                          <span className="block text-xs text-steel-500">{t("productDetailBrochurePdf")}</span>
                        </span>
                      </a>
                      <object
                        data={sharedBrochureUrl}
                        type="application/pdf"
                        aria-label={`${product.name} — ${t("productDetailTabBrochure").toLowerCase()}`}
                        className="h-[600px] w-full border border-steel-200"
                      >
                        <p className="flex h-[600px] w-full flex-col items-center justify-center gap-3 border border-steel-200 text-sm text-steel-500">
                          {t("productDetailBrochureNoPreview")}
                          <a
                            href={sharedBrochureUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-accent hover:text-accent-hover"
                          >
                            {t("productDetailOpenBrochureNewTab")}
                          </a>
                        </p>
                      </object>
                    </div>
                  )}

                  {effectiveTab === "accessories" && (
                    <ProductGrid groups={accessoryGroups} columns="2-4" />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sticky at the same offset as the product column beside it — on
              desktop, the product column (image/description/tabs) is
              usually taller than these 4 short cards, so without this the
              cards stay put while the taller sibling keeps scrolling,
              leaving blank page background beneath them until "Похожие
              товары" starts. Sticky keeps them in view the whole time the
              product column is, only scrolling away once that column's
              (and therefore this row's) bottom is reached. */}
          <div className="flex flex-col gap-6 xl:sticky xl:top-28 xl:w-[380px] xl:shrink-0">
            {/* Two fixes so this centers at any width, not just the narrow
                380px xl sidebar the min-h/alignment were originally tuned
                for: items-center (not items-start) centers the icon against
                the text block instead of pinning it to the top, and min-h
                is now xl-only — below xl these descriptions fit on one line
                (much more horizontal room once this stops being a narrow
                column), so an unconditional min-h-[32px] reserved space for
                a second line that was never there, leaving dead space under
                the text that no amount of centering alone could fix. */}
            <div className="grid grid-cols-1 gap-[5px]">
              <div className="flex items-center gap-2.5 border border-steel-200 py-[17px] pl-3 pr-[17px]">
                <Truck size={30} strokeWidth={1.6} className="shrink-0 text-accent" />
                <div>
                  <p className="text-sm font-semibold leading-5 text-ink">{t("productDetailDeliveryKz")}</p>
                  <p className="text-xs font-normal leading-4 text-steel-500 xl:min-h-[32px]">{t("productDetailDeliveryTerms")}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 border border-steel-200 py-[17px] pl-3 pr-[17px]">
                <Heart size={32} strokeWidth={1.6} className="shrink-0 text-accent" />
                <div>
                  <p className="text-sm font-semibold leading-5 text-ink">{t("productDetailCareTitle")}</p>
                  <p className="text-xs font-normal leading-4 text-steel-500 xl:min-h-[32px]">{t("productDetailCareDesc")}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 border border-steel-200 py-[17px] pl-3 pr-[17px]">
                <ShieldCheck size={30} strokeWidth={1.6} className="shrink-0 text-accent" />
                <div>
                  <p className="text-sm font-semibold leading-5 text-ink">{t("productDetailWarrantyTitle")}</p>
                  <p className="text-xs font-normal leading-4 text-steel-500 xl:min-h-[32px]">{t("productDetailWarrantyDesc")}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 border border-steel-200 py-[17px] pl-3 pr-[17px]">
                <GearWrench size={30} strokeWidth={1.6} className="shrink-0 text-accent" />
                <div>
                  <p className="text-sm font-semibold leading-5 text-ink">{t("service1Title")}</p>
                  <p className="text-xs font-normal leading-4 text-steel-500 xl:min-h-[32px]">{t("productDetailEngineerConsultDesc")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!catalogLoaded ? (
        // Reserves the same footprint the real carousel below occupies
        // while allProducts (and so similarGroups, computed from it) is
        // still empty — without this, the section renders at zero height,
        // then jumps in once the fetch resolves, shoving the footer down.
        // Cloudflare flagged exactly this footer element for CLS 0.555 on
        // product pages; same fix as ProductGridSkeleton, shaped for this
        // section's horizontal-scroll layout instead of a wrapped grid.
        <div className="container-industrial mt-16 md:mt-24" aria-hidden="true">
          <div className="xl:mx-auto xl:max-w-[1224px]">
            <div className="h-6 w-48 animate-pulse bg-steel-100" />
            <div className="mt-6 flex gap-0 overflow-hidden pl-px">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="-ml-px w-[220px] shrink-0 sm:w-[240px]">
                  <div className="flex h-full flex-col border border-steel-200 bg-white">
                    <div className="aspect-[4/3] animate-pulse bg-steel-100" />
                    <div className="flex flex-col gap-2 p-3 sm:p-6">
                      <div className="h-3 w-1/3 animate-pulse bg-steel-100" />
                      <div className="h-4 w-full animate-pulse bg-steel-100" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        similarGroups.length > 0 && (
        // container-industrial's own padding is OUTSIDE this 1224px cap, not
        // counted inside it — matching the product block above, where the
        // 1224px (820+24+380) is the flex row's actual content width, with
        // container-industrial's padding purely additional. Capping the
        // container-industrial element itself (as a previous version did)
        // measures 1224px including that padding, landing ~130px narrower
        // than the block above and breaking the edge alignment.
        <div className="container-industrial mt-16 md:mt-24">
          <div className="xl:mx-auto xl:max-w-[1224px]">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="font-sans text-lg font-semibold uppercase tracking-wide text-ink sm:text-xl">
              {t("productDetailSimilarItems")}
            </h2>
            <Link
              to={`/catalog?category=${product.category}`}
              className="shrink-0 text-xs font-semibold uppercase tracking-wide2 text-steel-500 underline decoration-steel-300 underline-offset-4 transition-colors duration-200 ease-engineered hover:text-accent hover:decoration-accent"
            >
              {t("blogViewAll")}
            </Link>
          </div>
          {/* A horizontal-scroll carousel, not a fixed grid — with 10
              candidates available, a shopper should be able to keep
              scrolling/pressing the arrow to see more, and a single fixed
              row of 4 would both hide the rest and, on mobile, force one
              card to eat the entire viewport width. Fixed card widths (not
              a fraction of the container) so a partial next card always
              peeks in as a scroll affordance, on every screen size. */}
          <div className="relative mt-6">
            <div
              ref={similarScrollRef}
              className="no-scrollbar -mt-10 flex snap-x snap-mandatory overflow-x-auto scroll-smooth pb-10 pl-px pt-10"
            >
              {/* Same shared-border overlap trick as ProductGrid (each card's
                  own full border, pulled 1px left to merge with its
                  neighbour's) so cards sit flush like the main catalog
                  grid, not spaced apart. */}
              {similarGroups.map((group) => (
                <div
                  key={group.key}
                  data-similar-card
                  className="-ml-px w-[220px] shrink-0 snap-start sm:w-[240px]"
                >
                  <ProductCard group={group} />
                </div>
              ))}
            </div>
            <div className="-mt-2 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => scrollSimilarBy(-1)}
                aria-label={t("productDetailPrevItems")}
                className="flex h-10 w-10 items-center justify-center border border-steel-300 text-ink transition-colors duration-200 ease-engineered hover:border-accent hover:text-accent"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={() => scrollSimilarBy(1)}
                aria-label={t("productDetailNextItems")}
                className="flex h-10 w-10 items-center justify-center border border-steel-300 text-ink transition-colors duration-200 ease-engineered hover:border-accent hover:text-accent"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
          </div>
        </div>
        )
      )}

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-ink/95"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="flex items-center justify-end p-4 text-white sm:p-6">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxOpen(false);
              }}
              aria-label={t("productDetailCloseImage")}
              className="flex h-10 w-10 items-center justify-center transition-colors duration-200 ease-engineered hover:text-accent"
            >
              <X size={24} />
            </button>
          </div>

          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4 pb-6 sm:px-16">
            {/* Only the image itself opts out of the backdrop's close-on-
                click — everything else in this area (the dark padding
                around it) should retract the lightbox, matching "press
                outside the image to close." The nav buttons stop
                propagation too, so paging through photos never closes it. */}
            <img
              src={images[activeImage] ?? ""}
              alt={product.name}
              draggable={false}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full object-contain"
            />

            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage((prev) => (prev - 1 + images.length) % images.length);
                  }}
                  aria-label={t("productDetailPrevItems")}
                  className="absolute left-2 flex h-11 w-11 items-center justify-center bg-white/10 text-white transition-colors duration-200 ease-engineered hover:bg-white/20 sm:left-6"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage((prev) => (prev + 1) % images.length);
                  }}
                  aria-label={t("productDetailNextItems")}
                  className="absolute right-2 flex h-11 w-11 items-center justify-center bg-white/10 text-white transition-colors duration-200 ease-engineered hover:bg-white/20 sm:right-6"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
          </div>

          {images.length > 1 && (
            <div className="flex items-center justify-center gap-2 pb-6">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImage(i);
                  }}
                  aria-label={`${t("productImageLabel")} ${i + 1}`}
                  className="flex h-4 w-4 items-center justify-center"
                >
                  <span
                    className={`transition-all duration-200 ease-engineered ${
                      activeImage === i ? "h-1.5 w-1.5 border-2 border-white" : "h-1 w-1 bg-white/50"
                    }`}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <a
        href={CONTACT_TELEGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Задать вопрос в Telegram"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center bg-accent text-white shadow-lg transition-colors duration-200 ease-engineered hover:bg-accent-hover sm:bottom-8 sm:right-8"
      >
        <Telegram size={30} strokeWidth={1.625} />
      </a>
    </section>
  );
}
